/**
 * Mobile-only top bar for Inbox navigation between list, conversation, and contact views.
 *
 * Where: `Inbox` page on small breakpoints.
 * Uses: MUI `AppBar`; title derives from `view` and `selectedChat`.
 * Behavior: back button hidden on list; shows prospect name in conversation mode.
 */
import React from 'react';
import {
  AppBar,
  Toolbar,
  Typography,
  IconButton,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
} from '@mui/icons-material';
import type { Chat } from '@/types/chat';
import { GlobalMessageResyncButton } from './GlobalMessageResyncButton';

interface MobileAppBarProps {
  view: 'list' | 'conversation' | 'details';
  selectedChat: Chat | null;
  onBack: () => void;
}

export const MobileAppBar: React.FC<MobileAppBarProps> = ({
  view,
  selectedChat,
  onBack,
}) => {
  const getTitle = () => {
    if (view === 'list') return 'Inbox';
    if (view === 'conversation') return selectedChat?.prospect.full_name || 'Conversation';
    return 'Contact Details';
  };

  return (
    <AppBar position="static" color="default" elevation={1}>
      <Toolbar>
        {view !== 'list' && (
          <IconButton edge="start" onClick={onBack} sx={{ mr: 2 }}>
            <ArrowBackIcon />
          </IconButton>
        )}
        <Typography variant="h6" sx={{ flex: 1 }}>
          {getTitle()}
        </Typography>
        {view === 'list' && <GlobalMessageResyncButton />}
      </Toolbar>
    </AppBar>
  );
};
